import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Table, message, Tag, Statistic, Empty, Spin, Tabs, Select, Descriptions, Space, List } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  TrophyOutlined,
  FileTextOutlined,
  CalendarOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  TeamOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  LineChartOutlined,
  DashboardOutlined,
  AlertOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TabPane } = Tabs;

const SupplierPage = () => {
  const { t } = useTranslation();

  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierDetails, setSupplierDetails] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [auditDates, setAuditDates] = useState([]);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [latestPerformance, setLatestPerformance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('urlToken');

  // ✅ Fetch suppliers list
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoading(true);
        console.log('🔄 Fetching suppliers...');
        
        const response = await axios.get(`${apiUrl}/api/Suppliers/suppliers-only`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log('📦 API Response:', response.data);
        console.log('📦 Response Type:', typeof response.data);
        console.log('📦 Is Array:', Array.isArray(response.data));

        // ✅ Response formatını kontrol et
        let suppliersData = [];
        
        if (Array.isArray(response.data)) {
          // Direkt array
          suppliersData = response.data;
        } else if (response.data?.suppliers && Array.isArray(response.data.suppliers)) {
          // Object içinde suppliers array'i
          suppliersData = response.data.suppliers;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          // Object içinde data array'i
          suppliersData = response.data.data;
        } else {
          console.warn('⚠️ Unexpected response format:', response.data);
          suppliersData = [];
        }

        console.log('✅ Suppliers loaded:', suppliersData.length);
        setSuppliers(suppliersData);

        if (suppliersData.length === 0) {
          message.warning(t('no_suppliers_available'));
        }
      } catch (error) {
        console.error('❌ Suppliers fetch error:', error);
        console.error('❌ Error response:', error.response?.data);
        message.error(t('data_fetch_error'));
        setSuppliers([]); // ✅ Hata durumunda boş array
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [apiUrl, token, t]);

  // ✅ Fetch supplier details
  const handleSupplierChange = async (supplierId) => {
    if (!supplierId) return;

    setSelectedSupplier(supplierId);
    setLoading(true);
    setActiveTab('overview');

    try {
      // 1. Supplier Details
      const detailsResponse = await axios.get(`${apiUrl}/api/Suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSupplierDetails(detailsResponse.data || null);

      // 2. Certificates
      try {
        const certificatesResponse = await axios.get(`${apiUrl}/api/Certificates/supplier/${supplierId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCertificates(Array.isArray(certificatesResponse.data) ? certificatesResponse.data : []);
      } catch (error) {
        console.warn('Certificates not found:', error);
        setCertificates([]);
      }

      // 3. Contacts
      try {
        const contactsResponse = await axios.get(`${apiUrl}/api/SupplierContact/supplier/${supplierId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const contactsData = contactsResponse.data?.contacts || contactsResponse.data || [];
        setContacts(Array.isArray(contactsData) ? contactsData : []);
      } catch (error) {
        console.warn('Contacts not found:', error);
        setContacts([]);
      }

      // 4. Audits
      try {
        const auditsResponse = await axios.get(`${apiUrl}/api/SupplierAudit/supplier/${supplierId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const auditsData = auditsResponse.data?.audits || auditsResponse.data || [];
        const audits = Array.isArray(auditsData) ? auditsData : [];
        setAuditDates(audits.sort((a, b) => moment(a.auditDate) - moment(b.auditDate)));
      } catch (error) {
        console.warn('Audits not found:', error);
        setAuditDates([]);
      }

      // 5. Performance History
      try {
        const performanceResponse = await axios.get(`${apiUrl}/api/SupplierEvaluations/${supplierId}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const perfData = performanceResponse.data?.performanceHistory || performanceResponse.data || [];
        const perfHistory = Array.isArray(perfData) ? perfData : [];
        setPerformanceHistory(perfHistory);
        setLatestPerformance(perfHistory[0] || null);
      } catch (error) {
        console.warn('Performance history not found:', error);
        setPerformanceHistory([]);
        setLatestPerformance(null);
      }
    } catch (error) {
      console.error('Data fetch error:', error);
      message.error(t('data_fetch_error'));
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper functions
  const getPerformanceColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#ff4d4f';
  };

  const getCertificateStatus = (cert) => {
    const daysUntilExpiry = moment(cert.certificateValidityDate).diff(moment(), 'days');
    if (cert.situation === '0') return { color: 'orange', text: t('pending'), icon: <ClockCircleOutlined /> };
    if (daysUntilExpiry < 0) return { color: 'red', text: t('expired'), icon: <WarningOutlined /> };
    if (daysUntilExpiry <= 30) return { color: 'gold', text: t('expiring_soon'), icon: <AlertOutlined /> };
    return { color: 'green', text: t('valid'), icon: <CheckCircleOutlined /> };
  };

  const getAuditStatus = (auditDate) => {
    const today = moment();
    const audit = moment(auditDate);
    if (audit.isBefore(today)) return { color: 'red', text: t('overdue') };
    if (audit.diff(today, 'days') <= 7) return { color: 'orange', text: t('upcoming') };
    return { color: 'green', text: t('scheduled') };
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ 
        background: '#fff',
        padding: '32px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderLeft: '4px solid #1890ff'
      }}>
        <h1 style={{ 
          fontSize: '32px', 
          fontWeight: '700', 
          color: '#262626', 
          margin: 0
        }}>
          <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
          {t('supplier_details')}
        </h1>
        <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: '15px' }}>
          {t('view_supplier_information')}
        </p>
      </div>

      {/* Supplier Selection */}
      <Card 
        style={{ 
          marginBottom: '24px', 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
            <IdcardOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            {t('select_supplier')}
          </div>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder={t('select_supplier_placeholder')}
            onChange={handleSupplierChange}
            loading={loading}
            size="large"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {Array.isArray(suppliers) && suppliers.map((supplier) => (
              <Option key={supplier.supplierId} value={supplier.supplierId}>
                {supplier.supplierName} ({supplier.supplierCode})
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {loading && (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" tip={t('loading')} />
        </div>
      )}

      {!loading && selectedSupplier && supplierDetails && (
        <>
          {/* Statistics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <Statistic
                  title={t('performance_score')}
                  value={latestPerformance?.totalScore || 0}
                  suffix="/ 100"
                  prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
                  valueStyle={{ color: getPerformanceColor(latestPerformance?.totalScore || 0), fontSize: '32px', fontWeight: '700' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card 
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <Statistic
                  title={t('supplier_class')}
                  value={latestPerformance?.supplierClass || 'N/A'}
                  prefix={<SafetyOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#1890ff' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card 
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <Statistic
                  title={t('total_certificates')}
                  value={certificates.length}
                  prefix={<FileTextOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#52c41a' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card 
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <Statistic
                  title={t('audit_frequency')}
                  value={latestPerformance?.auditFrequency || 0}
                  suffix={`/ ${t('year')}`}
                  prefix={<CalendarOutlined style={{ color: '#722ed1' }} />}
                  valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Main Content Tabs */}
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              size="large"
              tabBarStyle={{ 
                padding: '0 24px',
                margin: 0,
                background: '#fff',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              {/* Overview Tab */}
              <TabPane
                tab={
                  <span style={{ fontSize: '15px', fontWeight: '500' }}>
                    <IdcardOutlined style={{ marginRight: '8px' }} />
                    {t('overview')}
                  </span>
                }
                key="overview"
              >
                <div style={{ padding: '24px', background: '#fff' }}>
                  <Descriptions 
                    title={
                      <span style={{ fontSize: '18px', fontWeight: '600' }}>
                        <SafetyOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        {t('company_information')}
                      </span>
                    }
                    bordered
                    column={{ xs: 1, sm: 2, md: 3 }}
                    size="middle"
                  >
                    <Descriptions.Item label={t('supplier_name')}>
                      <strong>{supplierDetails.supplierName}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('supplier_code')}>
                      <Tag color="blue">{supplierDetails.supplierCode}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('category')}>
                      <Tag color="green">{supplierDetails.category}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('level')}>
                      <Tag color="purple">{supplierDetails.supplierLevel}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('contact')}>
                      <PhoneOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                      {supplierDetails.businessContact}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('email')}>
                      <MailOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                      {supplierDetails.businessEmail}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('address')} span={3}>
                      <EnvironmentOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
                      {supplierDetails.address}
                    </Descriptions.Item>
                  </Descriptions>

                  {latestPerformance && (
                    <Card 
                      title={
                        <span style={{ fontSize: '18px', fontWeight: '600' }}>
                          <LineChartOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                          {t('latest_performance')}
                        </span>
                      }
                      style={{ marginTop: '24px', borderRadius: '8px' }}
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                          <Statistic
                            title={t('total_score')}
                            value={latestPerformance.totalScore}
                            suffix="/ 100"
                            valueStyle={{ color: getPerformanceColor(latestPerformance.totalScore) }}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Statistic
                            title={t('supplier_class')}
                            value={latestPerformance.supplierClass}
                            valueStyle={{ color: '#1890ff' }}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Statistic
                            title={t('ppm')}
                            value={latestPerformance.ppm?.toFixed(2) || 'N/A'}
                            valueStyle={{ color: '#52c41a' }}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Statistic
                            title={t('risk_level')}
                            value={latestPerformance.totalRiskLevel}
                            valueStyle={{ 
                              color: latestPerformance.totalRiskLevel === 'Low' ? '#52c41a' : 
                                     latestPerformance.totalRiskLevel === 'Medium' ? '#faad14' : '#ff4d4f' 
                            }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  )}
                </div>
              </TabPane>

              {/* Certificates Tab */}
              <TabPane
                tab={
                  <span style={{ fontSize: '15px', fontWeight: '500' }}>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    {t('certificates')} ({certificates.length})
                  </span>
                }
                key="certificates"
              >
                <div style={{ padding: '24px', background: '#fff' }}>
                  <Table
                    dataSource={certificates}
                    columns={[
                      {
                        title: t('certificate_name'),
                        dataIndex: 'certificateName',
                        key: 'certificateName',
                        render: (text) => <strong style={{ fontSize: '14px' }}>{text}</strong>,
                      },
                      {
                        title: t('validity_date'),
                        dataIndex: 'certificateValidityDate',
                        key: 'certificateValidityDate',
                        render: (date) => (
                          <span style={{ fontSize: '14px' }}>
                            <CalendarOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
                            {moment(date).format('DD/MM/YYYY')}
                          </span>
                        ),
                        sorter: (a, b) => moment(a.certificateValidityDate).unix() - moment(b.certificateValidityDate).unix(),
                      },
                      {
                        title: t('days_remaining'),
                        key: 'daysRemaining',
                        align: 'center',
                        render: (_, record) => {
                          const days = moment(record.certificateValidityDate).diff(moment(), 'days');
                          return days >= 0 ? (
                            <Tag color={days <= 30 ? 'orange' : 'green'}>
                              {days} {t('days')}
                            </Tag>
                          ) : (
                            <Tag color="red">{t('expired')}</Tag>
                          );
                        },
                      },
                      {
                        title: t('status'),
                        key: 'status',
                        align: 'center',
                        render: (_, record) => {
                          const status = getCertificateStatus(record);
                          return (
                            <Tag icon={status.icon} color={status.color} style={{ fontSize: '13px' }}>
                              {status.text}
                            </Tag>
                          );
                        },
                      },
                    ]}
                    rowKey="certificateId"
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: <Empty description={t('no_certificates')} /> }}
                  />
                </div>
              </TabPane>

              {/* Contacts Tab */}
              <TabPane
                tab={
                  <span style={{ fontSize: '15px', fontWeight: '500' }}>
                    <TeamOutlined style={{ marginRight: '8px' }} />
                    {t('contacts')} ({contacts.length})
                  </span>
                }
                key="contacts"
              >
                <div style={{ padding: '24px', background: '#fff' }}>
                  <Table
                    dataSource={contacts}
                    columns={[
                      {
                        title: t('name'),
                        dataIndex: 'name',
                        key: 'name',
                        render: (text) => (
                          <span style={{ fontSize: '14px' }}>
                            <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                            <strong>{text}</strong>
                          </span>
                        ),
                      },
                      {
                        title: t('contact_type'),
                        dataIndex: 'contactTypeName',
                        key: 'contactTypeName',
                        render: (text) => <Tag color="blue" style={{ fontSize: '13px' }}>{text}</Tag>,
                      },
                      {
                        title: t('phone'),
                        dataIndex: 'phone',
                        key: 'phone',
                        render: (text) => (
                          <span style={{ fontSize: '14px' }}>
                            <PhoneOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                            {text}
                          </span>
                        ),
                      },
                      {
                        title: t('email'),
                        dataIndex: 'email',
                        key: 'email',
                        render: (text) => (
                          <span style={{ fontSize: '14px', color: '#1890ff' }}>
                            <MailOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                            {text}
                          </span>
                        ),
                      },
                    ]}
                    rowKey="contactId"
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: <Empty description={t('no_contacts')} /> }}
                  />
                </div>
              </TabPane>

              {/* Audits Tab */}
              <TabPane
                tab={
                  <span style={{ fontSize: '15px', fontWeight: '500' }}>
                    <CalendarOutlined style={{ marginRight: '8px' }} />
                    {t('audits')} ({auditDates.length})
                  </span>
                }
                key="audits"
              >
                <div style={{ padding: '24px', background: '#fff' }}>
                  {auditDates.length > 0 ? (
                    <List
                      itemLayout="horizontal"
                      dataSource={auditDates}
                      renderItem={(audit) => {
                        const status = getAuditStatus(audit.auditDate);
                        const isPast = moment(audit.auditDate).isBefore(moment());
                        
                        return (
                          <List.Item
                            style={{
                              padding: '16px',
                              marginBottom: '12px',
                              background: '#fafafa',
                              borderRadius: '8px',
                              border: '1px solid #f0f0f0'
                            }}
                          >
                            <List.Item.Meta
                              avatar={
                                <div style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '8px',
                                  background: status.color === 'red' ? '#fff1f0' : 
                                             status.color === 'orange' ? '#fff7e6' : '#f6ffed',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: `2px solid ${status.color === 'red' ? '#ffccc7' : 
                                                       status.color === 'orange' ? '#ffd591' : '#b7eb8f'}`
                                }}>
                                  <CalendarOutlined style={{ 
                                    fontSize: '20px', 
                                    color: status.color === 'red' ? '#ff4d4f' : 
                                           status.color === 'orange' ? '#faad14' : '#52c41a'
                                  }} />
                                </div>
                              }
                              title={
                                <Space>
                                  <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                    {moment(audit.auditDate).format('DD MMMM YYYY')}
                                  </span>
                                  <Tag color={status.color} style={{ fontSize: '13px' }}>
                                    {status.text}
                                  </Tag>
                                </Space>
                              }
                              description={
                                <Space direction="vertical" size={4}>
                                  <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                                    {isPast ? (
                                      <>
                                        <WarningOutlined style={{ marginRight: '4px' }} />
                                        {moment(audit.auditDate).fromNow()}
                                      </>
                                    ) : (
                                      <>
                                        <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                        {moment(audit.auditDate).fromNow()}
                                      </>
                                    )}
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#bfbfbf' }}>
                                    {moment(audit.auditDate).format('dddd')}
                                  </span>
                                </Space>
                              }
                            />
                          </List.Item>
                        );
                      }}
                    />
                  ) : (
                    <Empty 
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t('no_audits')} 
                    />
                  )}
                </div>
              </TabPane>

              {/* Performance History Tab */}
              <TabPane
                tab={
                  <span style={{ fontSize: '15px', fontWeight: '500' }}>
                    <LineChartOutlined style={{ marginRight: '8px' }} />
                    {t('performance_history')}
                  </span>
                }
                key="history"
              >
                <div style={{ padding: '24px', background: '#fff' }}>
                  <Table
                    dataSource={performanceHistory}
                    columns={[
                      {
                        title: t('period'),
                        dataIndex: 'period',
                        key: 'period',
                        render: (text) => <Tag color="blue" style={{ fontSize: '13px' }}>{text}</Tag>,
                      },
                      {
                        title: t('total_score'),
                        dataIndex: 'totalScore',
                        key: 'totalScore',
                        render: (score) => (
                          <span style={{ 
                            color: getPerformanceColor(score), 
                            fontWeight: '700',
                            fontSize: '16px'
                          }}>
                            {score}
                          </span>
                        ),
                        sorter: (a, b) => a.totalScore - b.totalScore,
                      },
                      {
                        title: t('supplier_class'),
                        dataIndex: 'supplierClass',
                        key: 'supplierClass',
                        render: (cls) => <Tag color="green" style={{ fontSize: '13px' }}>{cls}</Tag>,
                      },
                      {
                        title: t('ppm'),
                        dataIndex: 'ppm',
                        key: 'ppm',
                        render: (ppm) => (
                          <span style={{ fontSize: '14px' }}>
                            {ppm?.toFixed(2) || 'N/A'}
                          </span>
                        ),
                      },
                      {
                        title: t('risk_level'),
                        dataIndex: 'totalRiskLevel',
                        key: 'totalRiskLevel',
                        render: (level) => {
                          const color = level === 'Low' ? 'green' : level === 'Medium' ? 'orange' : 'red';
                          return <Tag color={color} style={{ fontSize: '13px' }}>{level}</Tag>;
                        },
                      },
                    ]}
                    rowKey="performanceDataId"
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: <Empty description={t('no_history')} /> }}
                  />
                </div>
              </TabPane>
            </Tabs>
          </Card>
        </>
      )}

      {!loading && !selectedSupplier && (
        <Card 
          style={{ 
            borderRadius: '12px', 
            textAlign: 'center', 
            padding: '80px 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: '16px', color: '#8c8c8c' }}>
                {t('select_supplier_to_view')}
              </span>
            }
          />
        </Card>
      )}
    </div>
  );
};

export default SupplierPage;
